import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';

/**
 * PageHeader — sayfa üst başlığı.
 *
 * Props:
 *   title       — büyük başlık
 *   subtitle    — küçük açıklama
 *   breadcrumbs — [{ label, to? }, ...] dizisi. Client-side navigate için
 *                 react-router Link kullanılır (iframe içinde full reload
 *                 yapmasın diye); to verilmezse static span render edilir.
 *   backTo      — "← Geri" butonu için hedef. String ise navigate(string),
 *                 -1 ise tarayıcı geçmişinde bir adım geri. Verilmezse
 *                 buton render edilmez.
 *   actions     — sağ üstte gösterilecek aksiyon kümesi (Button vb.)
 */
export default function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  actions,
  className,
}) {
  const navigate = useNavigate();
  const handleBack = () => {
    if (typeof backTo === 'number') navigate(backTo);
    else if (typeof backTo === 'string') navigate(backTo);
    else navigate(-1);
  };

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="flex items-start gap-3">
        {backTo !== undefined && (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Geri"
            title="Geri"
            className="mt-1 inline-flex items-center justify-center rounded-lg border border-neutral-200 bg-white p-1.5 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}
        <div>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
              {breadcrumbs.map((b, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <span className="text-neutral-300">/</span>}
                  {b.to ? (
                    <Link to={b.to} className="hover:text-primary-600">
                      {b.label}
                    </Link>
                  ) : (
                    <span>{b.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <h1 className="text-2xl font-bold text-neutral-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-neutral-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
